import { useState } from "react";
import { FiBriefcase, FiCamera } from "react-icons/fi";
import uploadFileToCloudinary from "../../../utils/uploadFileToCloudinary";

const CompanyFormFields = ({
  isEditing,
  register,
  errors,
  data,
  setValue,
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(data?.companyLogo || "");

  const activeInput =
    "border-gray-300 bg-white focus:ring-1 focus:ring-primary focus:outline-none";
  const inactiveInput = "border-gray-200 bg-gray-50 text-text-secondary";

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const localPreview = URL.createObjectURL(file);
    setPreview(localPreview);
    setUploading(true);

    try {
      const { uploadPromise } = uploadFileToCloudinary(file);

      const result = await uploadPromise;
      setValue("companyLogo", result.url);
      setPreview(result.url);
    } catch (err) {
      console.error("Logo Upload Failed:", err.message);
      alert(err.message || "Upload failed");
      setPreview(data?.companyLogo || "");
    }

    setUploading(false);
  };

  const getDefault = (field) =>
    data && data[field] !== undefined ? data[field] : "";

  return (
    <div className="space-y-5">
      {/* Logo Upload */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Company Logo
        </label>

        <div className="flex items-center gap-4">
          <label
            className="
              relative
              flex items-center justify-center
              w-20 h-20
              border-2 border-dashed border-gray-300
              rounded-lg cursor-pointer
              hover:border-primary transition-colors bg-gray-50
            "
          >
            {/* SPINNER */}
            {uploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-lg">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {/* PREVIEW IMAGE */}
            {preview ? (
              <img
                src={preview}
                className="w-full h-full object-cover rounded-lg"
                alt="Company Logo"
              />
            ) : (
              <FiCamera className="text-2xl text-text-muted" />
            )}

            {/* REAL INPUT */}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={!isEditing}
              onChange={handleLogoUpload}
            />
          </label>

          <div className="flex-1">
            <p className="text-sm text-text-secondary">
              Upload your company logo
            </p>
            <p className="text-xs text-text-muted mt-1">
              Recommended: Square, max 2MB
            </p>
          </div>
        </div>
      </div>

      {/* Company Name */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Company Name *
        </label>

        <div className="relative">
          <FiBriefcase
            className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted"
            size={18}
          />
          <input
            {...register("companyName", {
              required: "Company name is required",
            })}
            defaultValue={getDefault("companyName")}
            disabled={!isEditing}
            className={`w-full pl-11 pr-4 py-3 border rounded-lg transition-all ${
              isEditing ? activeInput : inactiveInput
            }`}
          />
        </div>

        {errors.companyName && (
          <p className="text-status-danger text-xs mt-1">
            {errors.companyName.message}
          </p>
        )}
      </div>

      {/* Two Column Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Founded Year
          </label>
          <input
            type="number"
            {...register("foundedYear")}
            defaultValue={getDefault("foundedYear")}
            disabled={!isEditing}
            placeholder="2010"
            className={`w-full px-4 py-3 border rounded-lg transition-all ${
              isEditing ? activeInput : inactiveInput
            }`}
            min={0}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Company RERA ID
          </label>
          <input
            {...register("companyReraId")}
            defaultValue={getDefault("reraId")}
            disabled={!isEditing}
            placeholder="RE-5642"
            className={`w-full px-4 py-3 border rounded-lg transition-all ${
              isEditing ? activeInput : inactiveInput
            }`}
          />
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Description
        </label>
        <textarea
          {...register("companyDescription")}
          defaultValue={getDefault("description")}
          disabled={!isEditing}
          rows={3}
          placeholder="Brief description..."
          className={`w-full px-4 py-3 border rounded-lg resize-none transition-all ${
            isEditing ? activeInput : inactiveInput
          }`}
        />
      </div>

      {/* Stats Section */}
      <h4 className="text-base font-semibold text-text-primary mt-4 mb-3">
        Company Statistics
      </h4>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: "Total Projects", name: "totalProjects" },
          { label: "Experience (Years)", name: "experienceYears" },
          { label: "Cities Covered", name: "citiesCovered" },
        ].map((item) => (
          <div key={item.name}>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {item.label}
            </label>
            <input
              type="number"
              min={0}
              {...register(item.name)}
              defaultValue={getDefault(item.name)}
              disabled={!isEditing}
              className={`w-full px-4 py-3 border rounded-lg transition-all ${
                isEditing ? activeInput : inactiveInput
              }`}
            />
          </div>
        ))}
      </div>

      {/* More Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        {[
          { label: "New Launch Projects", name: "newLaunch" },
          { label: "Ready To Move", name: "readyToMove" },
          { label: "Under Construction", name: "underConstruction" },
        ].map((item) => (
          <div key={item.name}>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {item.label}
            </label>
            <input
              type="number"
              min={0}
              {...register(item.name)}
              defaultValue={getDefault(item.name)}
              disabled={!isEditing}
              className={`w-full px-4 py-3 border rounded-lg transition-all ${
                isEditing ? activeInput : inactiveInput
              }`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default CompanyFormFields;
